/**
 * QR Code Display Page
 *
 * This component displays the QR code for customers to scan
 * and provides instructions for scanning
 */
import React, { useEffect } from "react";
import {
    Box,
    Typography,
    Button,
    useTheme,
    useMediaQuery,
    Alert,
    Card,
    CardContent,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import ScrollButtons from "../../../components/ScrollButtons";
import useBranchInfo from "../../../hooks/useBranchInfo";
import BranchHeader from "./BranchHeader";
import BranchNotFound, { BranchLoading } from "./BranchNotFound";
import CustomerFooter from "./CustomerFooter";
import useCustomerFlowTexts from "./useCustomerFlowTexts";

const QRCodeDisplayPage = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const {
        branchId,
        branchName,
        logoUrl,
        qrDataUrl,
        smsEnabled,
        loading,
        error,
        withBranch,
    } = useBranchInfo();
    const texts = useCustomerFlowTexts(branchId);

    // Fix scrolling issue by overriding global overflow hidden
    useEffect(() => {
        const rootElement = document.getElementById("root");
        const htmlElement = document.documentElement;

        document.body.style.overflow = "auto";
        document.body.style.height = "auto";
        htmlElement.style.overflow = "auto";
        htmlElement.style.height = "auto";

        if (rootElement) {
            rootElement.style.overflow = "auto";
            rootElement.style.height = "auto";
        }

        document.body.classList.add("scrollable-page");
        htmlElement.classList.add("scrollable-page");

        return () => {
            document.body.style.overflow = "hidden";
            document.body.style.height = "100%";
            htmlElement.style.overflow = "hidden";
            htmlElement.style.height = "100%";
            if (rootElement) {
                rootElement.style.overflow = "hidden";
                rootElement.style.height = "100%";
            }
            document.body.classList.remove("scrollable-page");
            htmlElement.classList.remove("scrollable-page");
        };
    }, []);

    const handleBackToChoice = () => {
        navigate(withBranch("/home/customer-choice"));
    };

    const handleSmsOption = () => {
        navigate(withBranch("/home/sms-customer-view"));
    };

    if (loading) return <BranchLoading />;
    if (error) return <BranchNotFound />;

    return (
        <>
            <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                px={2}
                sx={{ mt: { xs: 6, sm: 10 } }}
            >
                <BranchHeader
                    logoUrl={logoUrl}
                    branchName={branchName}
                    isMobile={isMobile}
                />

                <Typography
                    variant="h4"
                    color="primary"
                    textAlign="center"
                    mb={2}
                    sx={{ fontWeight: "bold" }}
                >
                    📱 Skann QR-koden
                </Typography>

                <Typography
                    variant="h6"
                    color="text.secondary"
                    textAlign="center"
                    mb={4}
                    sx={{ maxWidth: 500 }}
                >
                    {texts.qr_scan_text}
                </Typography>

                {qrDataUrl ? (
                    <Card sx={{ mb: 4, maxWidth: 400, width: "100%" }}>
                        <CardContent sx={{ textAlign: "center" }}>
                            <Typography variant="h6" color="primary" mb={2}>
                                {branchName}
                            </Typography>

                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                }}
                            >
                                <img
                                    src={qrDataUrl}
                                    alt="QR Code"
                                    style={{
                                        width: isMobile ? 200 : 250,
                                        height: isMobile ? 200 : 250,
                                        maxWidth: "100%",
                                        border: "1px solid #e0e0e0",
                                        borderRadius: "8px",
                                    }}
                                />
                            </Box>
                        </CardContent>
                    </Card>
                ) : (
                    <Alert severity="warning" sx={{ mb: 3, maxWidth: 400 }}>
                        Ingen QR-kode tilgjengelig
                    </Alert>
                )}

                <Box
                    sx={{
                        display: "flex",
                        gap: 2,
                        width: "100%",
                        maxWidth: 400,
                    }}
                >
                    <Button
                        variant="outlined"
                        onClick={handleBackToChoice}
                        sx={{ flex: 1 }}
                    >
                        Tilbake
                    </Button>
                    {smsEnabled && (
                        <Button
                            variant="contained"
                            color="secondary"
                            onClick={handleSmsOption}
                            sx={{ flex: 1 }}
                        >
                            Bruk SMS i stedet
                        </Button>
                    )}
                </Box>

                <CustomerFooter />
            </Box>
            <ScrollButtons />
        </>
    );
};

export default QRCodeDisplayPage;
